import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Edit3, Trash2, Save, Type, Star, ArrowUp, ArrowDown } from 'lucide-react';
import { 
  getAllFeedbackFormFields, 
  createFeedbackFormField, 
  updateFeedbackFormField, 
  deleteFeedbackFormField, 
  reorderFeedbackFormFields,
  FeedbackFormField,
  CreateFieldRequest,
  UpdateFieldRequest
} from '../lib/feedbackForm';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import PlatformIcon from './PlatformIcon';
import GoogleBusinessLocationsManager from './GoogleBusinessLocationsManager';

const FEEDBACK_GBP_CONNECT_KEY = 'feedback_gbp_connect';

function feedbackConnectPending() {
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get('feedback_connect') === '1' ||
      sessionStorage.getItem(FEEDBACK_GBP_CONNECT_KEY) === '1'
    );
  } catch {
    return false;
  }
}

function gbpOauthReturnInUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('gbp_oauth') === '1' || params.get('gbp_returning') === '1';
  } catch {
    return false;
  }
}

interface ConnectedGbpLocation {
  id: string;
  locationName: string | null;
  placeId: string | null;
}

/** Same write-a-review URL the Google Business page copies. Stored as a plain link. */
function googleReviewLinkForPlace(placeId: string) {
  return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`;
}

function connectionIdForReviewUrl(url: string, locations: ConnectedGbpLocation[]) {
  const trimmed = url.trim();
  if (!trimmed) return '';
  let placeId: string | null = null;
  try {
    const parsed = new URL(trimmed);
    placeId = parsed.searchParams.get('placeid') || parsed.searchParams.get('place_id');
  } catch {
    placeId = null;
  }
  if (placeId) {
    const match = locations.find((location) => location.placeId === placeId);
    if (match) return match.id;
  }
  const loose = locations.find((location) => location.placeId && trimmed.includes(location.placeId));
  return loose?.id || '';
}

interface CustomizeFeedbackFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FieldEditState {
  id: string | null;
  field_label: string;
  field_type: 'rating' | 'text';
  is_required: boolean;
}

const defaultFieldState: FieldEditState = {
  id: null,
  field_label: '',
  field_type: 'rating',
  is_required: false
};

export default function CustomizeFeedbackFormModal({ isOpen, onClose }: CustomizeFeedbackFormModalProps) {
  const { selectedBusiness } = useBusiness();
  const [fields, setFields] = useState<FeedbackFormField[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingField, setEditingField] = useState<FieldEditState>(defaultFieldState);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Google Reviews settings
  const [googleReviewsEnabled, setGoogleReviewsEnabled] = useState(false);
  const [googleReviewCondition, setGoogleReviewCondition] = useState('5'); // Default to 5 stars
  const [googleReviewUrl, setGoogleReviewUrl] = useState('');
  const [gbpLocations, setGbpLocations] = useState<ConnectedGbpLocation[]>([]);
  const [gbpLocationsLoaded, setGbpLocationsLoaded] = useState(false);
  const [showGbpConnect, setShowGbpConnect] = useState(feedbackConnectPending);
  const [googleSettingsLoaded, setGoogleSettingsLoaded] = useState(false);
  const persistedGoogleSettings = useRef<{ enabled: boolean; condition: string; url: string } | null>(null);
  const googleSettingsReady = useRef(false);
  const googleSaveSeq = useRef(0);

  // Load existing fields
  const loadFields = async () => {
    if (!selectedBusiness?.id) return;
    
    setLoading(true);
    try {
      const formFields = await getAllFeedbackFormFields(selectedBusiness.id);
      setFields(formFields);
    } catch (error) {
      console.error('Error loading feedback form fields:', error);
      toast.error('Failed to load feedback form fields');
    } finally {
      setLoading(false);
    }
  };

  // Load Google review settings
  const loadGoogleReviewSettings = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('businesses')
        .select('google_reviews_enabled, google_review_condition, google_review_url')
        .eq('id', selectedBusiness.id)
        .single();

      if (error) {
        console.error('Error loading Google review settings:', error);
        return;
      }

      if (data) {
        const next = {
          enabled: data.google_reviews_enabled || false,
          condition: String(data.google_review_condition || 5),
          url: data.google_review_url || '',
        };
        persistedGoogleSettings.current = next;
        googleSettingsReady.current = true;
        setGoogleReviewsEnabled(next.enabled);
        setGoogleReviewCondition(next.condition);
        setGoogleReviewUrl(next.url);
        setGoogleSettingsLoaded(true);
      }
    } catch (error) {
      console.error('Error loading Google review settings:', error);
    }
  };

  const loadConnectedGbpLocations = async () => {
    if (!selectedBusiness?.id || !supabase) {
      setGbpLocations([]);
      setGbpLocationsLoaded(true);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('google_business_connections')
        .select('id, location_name, place_id')
        .eq('business_id', selectedBusiness.id)
        .in('provider', ['ayrshare', 'google'])
        .eq('is_active', true)
        .order('location_name', { ascending: true });

      if (error) {
        console.error('Error loading Google Business connections:', error);
        setGbpLocations([]);
        return;
      }

      setGbpLocations(
        (data || []).map((row) => ({
          id: row.id,
          locationName: row.location_name,
          placeId: row.place_id,
        }))
      );
    } catch (error) {
      console.error('Error loading Google Business connections:', error);
      setGbpLocations([]);
    } finally {
      setGbpLocationsLoaded(true);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      googleSettingsReady.current = false;
      return;
    }
    try {
      sessionStorage.removeItem(FEEDBACK_GBP_CONNECT_KEY);
    } catch {
      /* ignore */
    }
    googleSettingsReady.current = false;
    persistedGoogleSettings.current = null;
    setGbpLocationsLoaded(false);
    setGoogleSettingsLoaded(false);
    loadFields();
    loadGoogleReviewSettings();
    loadConnectedGbpLocations();
    setShowAddForm(false);
    setEditingField(defaultFieldState);
  }, [isOpen, selectedBusiness?.id]);

  // One connected profile and no saved link yet: store that profile's review URL.
  useEffect(() => {
    if (!isOpen || !gbpLocationsLoaded || !googleSettingsLoaded || !googleReviewsEnabled) return;
    if (googleReviewUrl.trim()) return;
    const linkable = gbpLocations.filter((location) => location.placeId);
    if (linkable.length === 1 && linkable[0].placeId) {
      setGoogleReviewUrl(googleReviewLinkForPlace(linkable[0].placeId));
    }
  }, [isOpen, gbpLocationsLoaded, googleSettingsLoaded, googleReviewsEnabled, gbpLocations, googleReviewUrl]);

  // Google review settings save as they change. Questions already save on their own actions.
  useEffect(() => {
    if (!isOpen || !googleSettingsLoaded || !googleSettingsReady.current || !selectedBusiness?.id || !supabase) return;
    const persisted = persistedGoogleSettings.current;
    if (!persisted) return;

    const next = {
      enabled: googleReviewsEnabled,
      condition: googleReviewCondition,
      url: googleReviewUrl.trim(),
    };
    if (
      persisted.enabled === next.enabled &&
      persisted.condition === next.condition &&
      persisted.url === next.url
    ) {
      return;
    }

    const businessId = selectedBusiness.id;
    const seq = ++googleSaveSeq.current;
    void (async () => {
      try {
        const { error } = await supabase
          .from('businesses')
          .update({
            google_reviews_enabled: next.enabled,
            google_review_condition: parseInt(next.condition, 10),
            google_review_url: next.url,
          })
          .eq('id', businessId);

        if (seq !== googleSaveSeq.current) return;
        if (error) {
          console.error('Error saving Google review settings:', error);
          toast.error('Failed to save Google review settings');
          return;
        }
        persistedGoogleSettings.current = next;
      } catch (error) {
        if (seq !== googleSaveSeq.current) return;
        console.error('Error saving Google review settings:', error);
        toast.error('Failed to save Google review settings');
      }
    })();
  }, [isOpen, googleSettingsLoaded, googleReviewsEnabled, googleReviewCondition, googleReviewUrl, selectedBusiness?.id]);

  // Handle field creation
  const handleCreateField = async () => {
    if (!selectedBusiness?.id || !editingField.field_label.trim()) return;

    setSaving(true);
    try {
      const fieldKey = editingField.field_label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      
      const newFieldRequest: CreateFieldRequest = {
        field_key: fieldKey,
        field_label: editingField.field_label.trim(),
        field_type: editingField.field_type,
        is_required: editingField.is_required,
        display_order: fields.length + 1
      };

      const newField = await createFeedbackFormField(selectedBusiness.id, newFieldRequest);
      
      if (newField) {
        toast.success('Question added successfully');
        setEditingField(defaultFieldState);
        setShowAddForm(false);
        await loadFields();
      } else {
        toast.error('Failed to add question');
      }
    } catch (error) {
      console.error('Error creating field:', error);
      toast.error('Failed to add question');
    } finally {
      setSaving(false);
    }
  };

  // Handle field update
  const handleUpdateField = async () => {
    if (!selectedBusiness?.id || !editingField.id || !editingField.field_label.trim()) return;

    // Check if changing from rating to text would leave no rating fields
    const currentField = fields.find(f => f.id === editingField.id);
    if (currentField?.field_type === 'rating' && editingField.field_type === 'text') {
      const ratingFields = fields.filter(f => f.field_type === 'rating');
      if (ratingFields.length === 1) {
        toast.error('You must have at least one star rating question in your feedback form');
        return;
      }
    }

    setSaving(true);
    try {
      const updateRequest: UpdateFieldRequest = {
        field_label: editingField.field_label.trim(),
        field_type: editingField.field_type,
        is_required: editingField.is_required
      };

      const success = await updateFeedbackFormField(editingField.id, updateRequest);
      
      if (success) {
        toast.success('Question updated successfully');
        setEditingField(defaultFieldState);
        await loadFields();
      } else {
        toast.error('Failed to update question');
      }
    } catch (error) {
      console.error('Error updating field:', error);
      toast.error('Failed to update question');
    } finally {
      setSaving(false);
    }
  };

  // Handle field deletion
  const handleDeleteField = async (fieldId: string) => {
    if (!selectedBusiness?.id) return;

    // Check if this is the last rating field
    const ratingFields = fields.filter(f => f.field_type === 'rating');
    const fieldToDelete = fields.find(f => f.id === fieldId);
    
    if (fieldToDelete?.field_type === 'rating' && ratingFields.length === 1) {
      toast.error('You must have at least one star rating question in your feedback form');
      return;
    }

    if (!confirm('Are you sure you want to delete this question? This action cannot be undone.')) return;

    setSaving(true);
    try {
      const success = await deleteFeedbackFormField(fieldId);
      
      if (success) {
        toast.success('Question deleted successfully');
        await loadFields();
      } else {
        toast.error('Failed to delete question');
      }
    } catch (error) {
      console.error('Error deleting field:', error);
      toast.error('Failed to delete question');
    } finally {
      setSaving(false);
    }
  };

  // Handle field reordering
  const moveField = async (fieldId: string, direction: 'up' | 'down') => {
    const currentIndex = fields.findIndex(f => f.id === fieldId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= fields.length) return;

    const newFields = [...fields];
    const [movedField] = newFields.splice(currentIndex, 1);
    newFields.splice(newIndex, 0, movedField);

    if (!selectedBusiness?.id) return;

    const fieldOrders = newFields.map((field, index) => ({
      id: field.id,
      display_order: index + 1
    }));

    try {
      const success = await reorderFeedbackFormFields(selectedBusiness.id, fieldOrders);
      
      if (success) {
        setFields(newFields);
      } else {
        toast.error('Failed to reorder questions');
      }
    } catch (error) {
      console.error('Error reordering fields:', error);
      toast.error('Failed to reorder questions');
    }
  };

  const startEditField = (field: FeedbackFormField) => {
    setEditingField({
      id: field.id,
      field_label: field.field_label,
      field_type: field.field_type,
      is_required: field.is_required
    });
    setShowAddForm(false);
  };

  const startAddField = () => {
    setEditingField(defaultFieldState);
    setShowAddForm(true);
  };

  const cancelEdit = () => {
    setEditingField(defaultFieldState);
    setShowAddForm(false);
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Modal - always full screen (above AppSettingsModal) */}
      <div className="fixed inset-0 z-[100003] bg-white">
        <div
          className="bg-white w-full h-full flex flex-col overflow-hidden"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          {/* Header - iOS Style */}
          <div 
            className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200/60 bg-white"
            style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
          >
            <div className="flex-1 min-w-0">
              <h2 className="text-base sm:text-xl font-semibold text-gray-900">Customize Feedback Form</h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5 hidden sm:block">Create questions that matter to your business</p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-2 text-gray-500 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors touch-manipulation ml-2"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50">
            {loading ? (
              <div className="flex items-center justify-center py-12 sm:py-16">
                <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-sm sm:text-base text-gray-600">Loading your feedback form...</span>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto space-y-4 sm:space-y-5">
                {/* Questions List */}
                {fields.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-900">Your Questions</h3>
                      <span className="text-xs sm:text-sm text-gray-500">{fields.length} question{fields.length !== 1 ? 's' : ''}</span>
                    </div>
                    
                    <div className="space-y-1.5 sm:space-y-2">
                      {fields.map((field, index) => (
                        <div key={field.id} className="bg-white border border-gray-200 rounded-lg px-2.5 py-2 sm:px-3 sm:py-2 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="bg-blue-100 text-blue-800 text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 tabular-nums">
                                Q{index + 1}
                              </span>
                              <span className="text-sm font-medium text-gray-900 truncate">{field.field_label}</span>
                              {field.is_required && (
                                <span className="text-red-600 text-[10px] font-medium bg-red-50 px-1.5 py-0.5 rounded shrink-0">Required</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5 pl-0.5">
                              {field.field_type === 'rating' ? (
                                <>
                                  <Star className="h-3 w-3" />
                                  <span>Star Rating (1-5)</span>
                                </>
                              ) : (
                                <>
                                  <Type className="h-3 w-3" />
                                  <span>Text Response</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-0.5 shrink-0">
                            {/* Move buttons */}
                            <div className="flex flex-col">
                              <button
                                onClick={() => moveField(field.id, 'up')}
                                disabled={index === 0}
                                aria-label="Move up"
                                className={`p-0.5 rounded transition-colors ${
                                  index === 0 
                                    ? 'text-gray-300 cursor-not-allowed' 
                                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                }`}
                              >
                                <ArrowUp className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => moveField(field.id, 'down')}
                                disabled={index === fields.length - 1}
                                aria-label="Move down"
                                className={`p-0.5 rounded transition-colors ${
                                  index === fields.length - 1 
                                    ? 'text-gray-300 cursor-not-allowed' 
                                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                }`}
                              >
                                <ArrowDown className="h-3 w-3" />
                              </button>
                            </div>
                            {/* Edit button */}
                            <button
                              onClick={() => startEditField(field)}
                              aria-label="Edit question"
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            >
                              <Edit3 className="h-3.5 w-3.5" />
                            </button>
                            {/* Delete button */}
                            <button
                              onClick={() => handleDeleteField(field.id)}
                              disabled={field.field_type === 'rating' && fields.filter(f => f.field_type === 'rating').length === 1}
                              aria-label="Delete question"
                              className={`p-1.5 rounded transition-colors ${
                                field.field_type === 'rating' && fields.filter(f => f.field_type === 'rating').length === 1
                                  ? 'text-gray-300 cursor-not-allowed'
                                  : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                              }`}
                              title={
                                field.field_type === 'rating' && fields.filter(f => f.field_type === 'rating').length === 1
                                  ? 'Cannot delete the last star rating question'
                                  : 'Delete question'
                              }
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Question Button or Empty State */}
              {!showAddForm && !editingField.id && (
                <div className="text-center mb-8">
                  {fields.length === 0 ? (
                    <div className="py-12">
                      <div className="text-gray-400 mb-4">
                        <Star className="h-12 w-12 mx-auto" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No questions yet</h3>
                      <p className="text-gray-500 mb-6">Start by adding your first feedback question</p>
                      <button
                        onClick={startAddField}
                        className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                      >
                        <Plus className="h-5 w-5 mr-2" />
                        Add Your First Question
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={startAddField}
                      className="inline-flex items-center px-6 py-3 border border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 transition-colors"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Add Another Question
                    </button>
                  )}
                </div>
              )}

              {/* Add/Edit Form */}
              {(showAddForm || editingField.id) && (
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200 mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    {editingField.id ? 'Edit Question' : 'Add New Question'}
                  </h4>
                  
                  <div className="space-y-4">
                    {/* Question Text */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        What do you want to ask your customers?
                      </label>
                      <input
                        type="text"
                        value={editingField.field_label}
                        onChange={(e) => setEditingField(prev => ({ ...prev, field_label: e.target.value }))}
                        placeholder="e.g., How would you rate the quality of our service?"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {/* Response Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        How should customers respond?
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          editingField.field_type === 'rating' 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}>
                          <input
                            type="radio"
                            name="field_type"
                            value="rating"
                            checked={editingField.field_type === 'rating'}
                            onChange={(e) => setEditingField(prev => ({ ...prev, field_type: e.target.value as 'rating' | 'text' }))}
                            className="sr-only"
                          />
                          <div className="flex flex-col items-center text-center">
                            <Star className="h-6 w-6 mb-2 text-yellow-500" />
                            <span className="font-medium text-gray-900">Star Rating</span>
                            <span className="text-xs text-gray-500 mt-1">1 to 5 stars</span>
                          </div>
                        </label>
                        
                        <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          editingField.field_type === 'text' 
                            ? 'border-blue-500 bg-blue-50' 
                            : (() => {
                                if (editingField.id) {
                                  const currentField = fields.find(f => f.id === editingField.id);
                                  const isLastRatingField = currentField?.field_type === 'rating' && fields.filter(f => f.field_type === 'rating').length === 1;
                                  return isLastRatingField 
                                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                                    : 'border-gray-200 hover:border-gray-300';
                                }
                                return 'border-gray-200 hover:border-gray-300';
                              })()
                        }`}>
                          <input
                            type="radio"
                            name="field_type"
                            value="text"
                            checked={editingField.field_type === 'text'}
                            onChange={(e) => setEditingField(prev => ({ ...prev, field_type: e.target.value as 'rating' | 'text' }))}
                            disabled={(() => {
                              if (editingField.id) {
                                const currentField = fields.find(f => f.id === editingField.id);
                                return currentField?.field_type === 'rating' && fields.filter(f => f.field_type === 'rating').length === 1;
                              }
                              return false;
                            })()}
                            className="sr-only"
                          />
                          <div className="flex flex-col items-center text-center">
                            <Type className="h-6 w-6 mb-2 text-blue-500" />
                            <span className="font-medium text-gray-900">Text Response</span>
                            <span className="text-xs text-gray-500 mt-1">Written feedback</span>
                          </div>
                        </label>
                      </div>
                      {(() => {
                        if (editingField.id) {
                          const currentField = fields.find(f => f.id === editingField.id);
                          const isLastRatingField = currentField?.field_type === 'rating' && fields.filter(f => f.field_type === 'rating').length === 1;
                          return isLastRatingField ? (
                            <p className="text-xs text-amber-600 mt-2 flex items-center">
                              <span className="mr-1">⚠️</span>
                              You must have at least one star rating question in your feedback form
                            </p>
                          ) : null;
                        }
                        return null;
                      })()}
                    </div>

                    {/* Required Toggle */}
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editingField.is_required}
                          onChange={(e) => setEditingField(prev => ({ ...prev, is_required: e.target.checked }))}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-700">
                          Make this question required
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1 ml-7">
                        Customers must answer this question before submitting
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={cancelEdit}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={editingField.id ? handleUpdateField : handleCreateField}
                        disabled={saving || !editingField.field_label.trim()}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {saving ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            {editingField.id ? 'Update Question' : 'Add Question'}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Google Reviews */}
              <div className="rounded-xl border border-gray-200 bg-white p-4 mb-8">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <h4 className="text-sm font-semibold text-gray-900">Google reviews</h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Ask customers who rate you highly to leave a Google review
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setGoogleReviewsEnabled(!googleReviewsEnabled)}
                    aria-pressed={googleReviewsEnabled}
                    aria-label="Ask for Google reviews"
                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                      googleReviewsEnabled ? 'bg-green-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        googleReviewsEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {googleReviewsEnabled && (
                  <div className="mt-4 space-y-4 border-t border-gray-100 pt-4">
                    {!gbpLocationsLoaded ? (
                      <p className="text-xs text-gray-500">Loading connected Google businesses…</p>
                    ) : gbpLocations.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-3">
                        <div className="flex items-center gap-2.5">
                          <PlatformIcon platform="google_business" size={28} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900">Connect Google Business</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Connect a profile to choose its review link.
                            </p>
                          </div>
                        </div>
                        {showGbpConnect && selectedBusiness?.id ? (
                          <div className="mt-3">
                            <GoogleBusinessLocationsManager
                              businessId={selectedBusiness.id}
                              autoStart={!gbpOauthReturnInUrl()}
                              returnContext="feedback"
                              onChanged={() => {
                                void loadConnectedGbpLocations();
                              }}
                            />
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setShowGbpConnect(true)}
                            className="mt-3 inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Connect Google Business
                          </button>
                        )}
                      </div>
                    ) : (
                      <div>
                        <p className="text-xs font-medium text-gray-700 mb-1.5">Connected Google businesses</p>
                        <ul className="space-y-1.5">
                          {gbpLocations.map((location) => (
                            <li key={location.id} className="flex items-center gap-2 min-w-0">
                              <PlatformIcon platform="google_business" size={18} />
                              <span className="text-sm text-gray-800 truncate">
                                {location.locationName || 'Untitled location'}
                              </span>
                              {!location.placeId && (
                                <span className="text-[11px] text-gray-400 shrink-0">No review link</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Ask when the rating is
                      </label>
                      <select
                        value={googleReviewCondition}
                        onChange={(e) => setGoogleReviewCondition(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      >
                        <option value="5">5 stars</option>
                        <option value="4">4 stars or higher</option>
                        <option value="3">3 stars or higher</option>
                      </select>
                    </div>

                    {gbpLocations.some((location) => location.placeId) && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Google review link <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={connectionIdForReviewUrl(googleReviewUrl, gbpLocations)}
                          onChange={(e) => {
                            const location = gbpLocations.find((item) => item.id === e.target.value);
                            if (location?.placeId) {
                              setGoogleReviewUrl(googleReviewLinkForPlace(location.placeId));
                            }
                          }}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value="">Select a Google Business</option>
                          {gbpLocations.filter((location) => location.placeId).map((location) => (
                            <option key={location.id} value={location.id}>
                              {location.locationName || 'Untitled location'}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1.5">
                          {googleReviewUrl.trim() && !connectionIdForReviewUrl(googleReviewUrl, gbpLocations)
                            ? 'The review link already saved stays in place until you pick a profile.'
                            : 'Customers who meet the rating open this profile’s review link.'}
                        </p>
                      </div>
                    )}

                    {gbpLocationsLoaded && !gbpLocations.some((location) => location.placeId) && googleReviewUrl.trim() && (
                      <p className="text-xs text-gray-500">
                        A review link is already saved and will keep being used until you pick a connected profile.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
} 
 